/**
 * Static example data for Financial Research Blueprint showcase
 * Topic: "Is NVIDIA (NVDA) overvalued at current levels?"
 */

import type {
  FinancialStreamingState, FinancialAgentProgress, DebateMessage, MediatorCompilation,
} from '../FinancialLogicTree';
import type { FinancialKillSheet, AnchoredSnapshot } from '../FinancialAnalysisPanel';

const EXAMPLE_SNAPSHOT: AnchoredSnapshot = {
  ticker: 'NVDA',
  companyName: 'NVIDIA Corporation',
  asOf: '2026-02-06T16:00:00Z',
  status: 'success',
  price: 138.25,
  priceFormatted: '$138.25',
  currency: 'USD',
  changePercent: 2.47,
  changePercentFormatted: '+2.47%',
  priceMovement: 'up',
  marketCap: 3380000000000,
  marketCapFormatted: '$3.38T',
  peTTM: 52.8,
  peFormatted: '52.8x',
  epsTTM: 2.62,
  epsFormatted: '$2.62',
  peForward: 32.1,
  yearHigh: 153.13,
  yearLow: 75.61,
  fiftyTwoWeekPosition: 0.81,
  volume: 287400000,
  avgVolume: 312000000,
  provider: 'FMP',

  // Analyst Sentiment (Yahoo Finance)
  yfRecommendations: {
    strong_buy: 18,
    buy: 24,
    hold: 12,
    sell: 3,
    strong_sell: 1,
    total: 58,
    consensus: 'Buy',
  },
  numAnalysts: 58,
  consensusRating: 'Buy',
  avgPriceTarget: 165.50,
  priceTargetUpside: 19.7,
  yfPriceTargets: {
    current: 138.25,
    mean: 165.50,
    low: 90.00,
    high: 220.00,
    median: 162.00,
    num_analysts: 58,
  },

  // Financial Health Scores (FMP)
  altmanZScore: 8.45,
  altmanZRisk: 'Safe',
  piotroskiScore: 7,
  piotroskiStrength: 'Strong',

  // Market Sentiment (Yahoo Finance)
  yfKeyStats: {
    short_ratio: 1.18,
    held_by_institutions: 0.674,
    held_by_insiders: 0.039,
  },

  // Recent News Headlines
  recentNews: [
    {
      title: 'NVIDIA Blackwell B200 GPUs See Record Pre-Orders from Hyperscalers',
      url: 'https://finance.yahoo.com/news/nvidia-blackwell-b200',
      publishedDate: '2026-02-05',
      source: 'yahoo_finance',
    },
    {
      title: 'NVIDIA Data Center Revenue Surges 94% YoY in Q3 FY2025',
      url: 'https://finance.yahoo.com/news/nvidia-q3-earnings',
      publishedDate: '2026-02-04',
      source: 'yahoo_finance',
    },
    {
      title: 'Saudi Arabia Signs $12B AI Infrastructure Deal with NVIDIA',
      url: 'https://finance.yahoo.com/news/saudi-nvidia-deal',
      publishedDate: '2026-02-03',
      source: 'yahoo_finance',
    },
    {
      title: 'DeepSeek R1 Efficiency Breakthrough Raises Questions About GPU Demand',
      url: 'https://finance.yahoo.com/news/deepseek-r1-gpu-demand',
      publishedDate: '2026-02-02',
      source: 'yahoo_finance',
    },
    {
      title: 'NVIDIA CUDA Ecosystem Reaches 5 Million Active Developers',
      url: 'https://finance.yahoo.com/news/nvidia-cuda-developers',
      publishedDate: '2026-01-30',
      source: 'yahoo_finance',
    },
    {
      title: 'AMD MI400 Launch Timeline Pushed to Late 2026, Analysts Say',
      url: 'https://finance.yahoo.com/news/amd-mi400-delay',
      publishedDate: '2026-01-28',
      source: 'yahoo_finance',
    },
  ],

  // Historical Performance (5-Year)
  historicalData: {
    return_ytd: 12.3,
    return_1y: 180.5,
    return_3y: 245.8,
    return_5y: 1250.2,
    volatility_1y: 42.5,
    high_52w: 153.13,
    low_52w: 75.61,
    avg_volume_3m: 312000000,
    start_date: '2021-02-06',
    end_date: '2026-02-06',
    data_points: 1258,
  },
};

const BULL_AGENTS: FinancialAgentProgress[] = [
  {
    side: 'bull', index: 0, roleId: 'value_investor', roleName: 'Value Investor',
    model: 'Claude 4 Sonnet', provider: 'anthropic', status: 'complete',
    output: '**Forward P/E of 32x is reasonable for a company growing earnings at 50%+ YoY.** NVIDIA\'s data center revenue grew 94% in fiscal Q3 2025, driven by insatiable demand for H100/H200 GPUs and the new Blackwell architecture. PEG ratio of 0.64 suggests undervaluation relative to growth.\n\nKey thesis: The AI infrastructure buildout is still in early innings. Hyperscaler capex budgets for 2026 total $280B+, up from $190B in 2024. NVIDIA captures 80%+ of AI training GPU market share with no credible competitor at scale.\n\n[S1] FMP Financial Data - NVDA Q3 FY2025 Earnings\n[S2] SEC Filing - NVDA 10-Q November 2025',
    citationCount: 8,
    citations: [
      { id: 'S1', source_type: 'FMP', title: 'NVDA Q3 FY2025 Financial Statements', url: 'https://fmp.com/nvda' },
      { id: 'S2', source_type: 'SEC', title: 'NVIDIA 10-Q Filing Nov 2025', url: 'https://sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=1045810' },
    ],
    latency: 9.2,
  },
  {
    side: 'bull', index: 1, roleId: 'growth_strategist', roleName: 'Growth Strategist',
    model: 'GPT-4.1', provider: 'openai', status: 'complete',
    output: '**NVIDIA is transitioning from a chip company to a platform company.** CUDA ecosystem lock-in creates a moat comparable to Microsoft Windows in the 1990s. Software revenue (AI Enterprise, Omniverse) is growing at 150%+ and carries 85%+ gross margins, well above hardware margins.\n\nBlackwell B200/GB200 architecture represents a generational leap: 4x training performance per watt over H100. Every major cloud provider has placed multi-billion dollar orders. The inference market (10x larger TAM than training) is just beginning to ramp.\n\n[S3] Gartner AI Infrastructure Forecast 2026\n[S4] NVIDIA GTC 2025 Keynote Analysis',
    citationCount: 7,
    citations: [
      { id: 'S3', source_type: 'WEB', title: 'Gartner: AI Infrastructure Spending Forecast', url: 'https://gartner.com/ai-forecast-2026' },
      { id: 'S4', source_type: 'WEB', title: 'GTC 2025 Blackwell Architecture Deep Dive' },
    ],
    latency: 8.1,
  },
  {
    side: 'bull', index: 2, roleId: 'macro_economist', roleName: 'Macro Economist',
    model: 'Gemini 2.5 Pro', provider: 'google', status: 'complete',
    output: '**Macro tailwinds strongly favor NVIDIA.** Global AI investment is a secular trend resistant to typical cyclical pressures. Government AI initiatives (US CHIPS Act, EU AI Act compliance spending, China domestic AI push) create a multi-sovereign demand floor.\n\nMonetary policy is supportive: Fed rate cuts and easing financial conditions reduce the discount rate on high-growth equities. The AI productivity thesis is gaining validation from enterprise adoption data showing 15-40% efficiency gains in early deployments.\n\n[S5] Federal Reserve Economic Data\n[S6] McKinsey Global AI Adoption Survey 2025',
    citationCount: 6,
    citations: [
      { id: 'S5', source_type: 'WEB', title: 'FRED: Tech Sector Capital Expenditure Data' },
      { id: 'S6', source_type: 'WEB', title: 'McKinsey AI Adoption & Productivity Report' },
    ],
    latency: 7.5,
  },
];

const BEAR_AGENTS: FinancialAgentProgress[] = [
  {
    side: 'bear', index: 0, roleId: 'risk_analyst', roleName: 'Risk Analyst',
    model: 'Claude 4 Sonnet', provider: 'anthropic', status: 'complete',
    output: '**At $3.4T market cap, NVDA prices in perfection with no margin of safety.** Historical precedent is brutal: Cisco peaked at $556B in 2000 (70x earnings) during a similar "paradigm shift" narrative. NVIDIA\'s current 53x trailing P/E exceeds Cisco\'s peak multiples when adjusted for growth sustainability.\n\nConcentration risk is severe: top 5 customers (Microsoft, Meta, Google, Amazon, Oracle) represent ~55% of data center revenue. Any slowdown in hyperscaler capex directly impacts NVIDIA at scale. The DeepSeek R1 efficiency breakthrough showed that AI can advance with less compute.\n\n[S7] SEC Edgar - NVDA Customer Concentration Analysis\n[S8] Historical Bubble Comparison: Cisco 2000 vs NVDA 2026',
    citationCount: 9,
    citations: [
      { id: 'S7', source_type: 'SEC', title: 'NVDA 10-K Customer Concentration Data' },
      { id: 'S8', source_type: 'WEB', title: 'Dot-com Bubble Valuation Comparisons' },
    ],
    latency: 8.8,
  },
  {
    side: 'bear', index: 1, roleId: 'market_technician', roleName: 'Market Technician',
    model: 'GPT-4.1', provider: 'openai', status: 'complete',
    output: '**Technical indicators flash caution.** RSI at 72 indicates overbought conditions. The stock is 81% through its 52-week range with volume declining on recent up-moves, a classic distribution pattern. Put/call ratio has been rising for 3 consecutive weeks.\n\nThe Blackwell cycle is largely priced in. Historical analysis of NVIDIA\'s product cycles shows a consistent pattern: stock rallies into new architecture launch, then consolidates 15-25% as execution reality meets elevated expectations. Current positioning mirrors the H100-to-H200 transition in early 2024.\n\n[S9] Finnhub Technical Indicators - NVDA\n[S10] Options Flow Analysis - CBOE Data',
    citationCount: 6,
    citations: [
      { id: 'S9', source_type: 'WEB', title: 'NVDA Technical Analysis - RSI, MACD, Volume' },
      { id: 'S10', source_type: 'WEB', title: 'NVDA Options Flow and Put/Call Ratios' },
    ],
    latency: 7.3,
  },
];

const DEBATE_MESSAGES: DebateMessage[] = [
  {
    side: 'bull', round: 1, timestamp: Date.now() - 90000,
    text: 'NVIDIA\'s forward P/E of 32x is justified by 50%+ earnings growth. The Blackwell architecture extends their lead by 2+ years. AI infrastructure spending is a secular trend, not cyclical.',
  },
  {
    side: 'bear', round: 1, timestamp: Date.now() - 80000,
    text: 'At $3.4T market cap, the stock prices in years of flawless execution. Customer concentration in 5 hyperscalers is a structural risk. The DeepSeek breakthrough proves AI can advance with less compute, undermining the "insatiable demand" thesis.',
  },
  {
    side: 'bull', round: 2, timestamp: Date.now() - 70000,
    text: 'DeepSeek validates software efficiency gains, but inference scaling requires MORE GPUs, not fewer. The inference TAM is 10x the training market. CUDA lock-in and platform revenue diversify beyond hardware cyclicality.',
  },
  {
    side: 'bear', round: 2, timestamp: Date.now() - 60000,
    text: 'CUDA lock-in is overstated. AMD ROCm, Intel Gaudi, and custom ASICs (Google TPU v5, Amazon Trainium) are credible alternatives with improving software stacks. Historical precedent (Intel in 2000) shows dominant chipmakers can lose share rapidly once alternatives mature.',
  },
  {
    side: 'mediator', timestamp: Date.now() - 50000,
    text: 'After two rounds of structured debate, the bull case is stronger on near-term fundamentals (12-18 months) while the bear case raises valid long-term structural concerns (3-5 year horizon). The key unresolved question is whether NVIDIA\'s competitive moat is durable or cyclical. Ruling: BULL prevails on fundamentals, but position sizing should reflect elevated concentration and valuation risk.',
  },
];

const MEDIATOR_COMPILATION: MediatorCompilation = {
  bullIntensity: 72,
  bearIntensity: 58,
  bullArguments: [
    'Forward P/E of 32x reasonable for 50%+ growth; PEG ratio of 0.64 implies undervaluation',
    'Blackwell architecture provides 2+ year competitive advantage with 4x performance per watt',
    'Inference market (10x training TAM) is in early innings of monetization',
    'CUDA ecosystem lock-in creates platform-level moat similar to Windows in 1990s',
    'Government AI initiatives create sovereign demand floor resistant to business cycles',
  ],
  bearArguments: [
    'At $3.4T market cap, no margin of safety; historical parallels to Cisco 2000 are concerning',
    'Top 5 customers represent ~55% of data center revenue; extreme concentration risk',
    'Technical indicators show overbought conditions (RSI 72) with declining volume on rallies',
    'DeepSeek R1 proves AI can advance with less compute; threatens "insatiable demand" thesis',
    'AMD ROCm, Google TPU v5, Amazon Trainium provide credible alternative paths within 2-3 years',
  ],
  verdict: 'The bull case prevails on near-term fundamentals: NVIDIA\'s competitive position is unassailable for the next 12-18 months, with Blackwell demand exceeding supply. However, the bear side raises valid structural concerns about long-term moat durability and extreme valuation multiples. Verdict: Moderately bullish with position sizing caveats.',
};

const KILL_SHEET: FinancialKillSheet = {
  ticker: 'NVDA',
  analysis_date: '2026-02-06',
  winning_side: 'bull',
  confidence: 0.72,
  bull_thesis: 'NVIDIA\'s forward P/E of 32x is justified by 50%+ earnings growth, Blackwell architecture dominance, and early-stage inference market monetization. CUDA ecosystem lock-in and $280B+ hyperscaler capex provide strong near-term visibility. The AI infrastructure buildout is a secular trend with sovereign demand support.',
  bear_thesis: 'At $3.4T market cap, NVIDIA prices in perfection with severe concentration risk (top 5 customers = 55% revenue). Historical parallels to Cisco 2000 are uncomfortable. Competitive alternatives (AMD, Google TPU, Amazon Trainium) are maturing, and DeepSeek proved AI can advance with less compute. Technical indicators flash distribution.',
  kill_sheet_reason: 'Bull case prevails on near-term execution certainty: Blackwell demand exceeds supply through FY2027, inference market is just beginning to ramp, and no competitor can match CUDA ecosystem depth within 18 months. Bear structural concerns are valid for 3-5 year horizon but do not override immediate fundamentals.',
  rejected_arguments: [
    {
      side: 'bear', agent_role: 'Market Technician', agent_model: 'GPT-4.1',
      argument_summary: 'Technical overbought signals (RSI 72, declining volume) predict near-term correction',
      rejection_reason: 'Insufficient for fundamental thesis',
      rejection_detail: 'Technical indicators have poor predictive power for structurally-driven moves. NVIDIA\'s RSI has been above 70 for 35% of trading days over the past 2 years while the stock appreciated 180%.',
      counter_evidence: 'Historical backtesting shows RSI>70 sells on NVDA generated 12% average opportunity cost over 6 months',
      sources_cited: ['Finnhub Technical Data', 'NVDA Historical RSI Analysis'],
    },
    {
      side: 'bull', agent_role: 'Macro Economist', agent_model: 'Gemini 2.5 Pro',
      argument_summary: 'Fed rate cuts create favorable discount rate environment for high-growth equities',
      rejection_reason: 'Generic macro argument not specific to NVIDIA',
      rejection_detail: 'Monetary policy tailwind applies equally to all growth stocks and does not differentiate NVIDIA\'s specific valuation case. The argument lacks company-specific analytical value.',
      sources_cited: ['Federal Reserve Economic Data'],
    },
  ],
  caveats: 'Analysis assumes no major geopolitical disruption to Taiwan semiconductor supply chain. US-China technology restrictions could create bifurcated markets not captured in current models. Earnings estimates assume hyperscaler capex budgets are not revised downward.',
  data_sources: {
    'FMP Financial Data': '2026-02-06T14:30:00Z',
    'SEC EDGAR (10-Q)': '2025-11-22T00:00:00Z',
    'Finnhub Market Data': '2026-02-06T16:00:00Z',
    'Web Research (12 sources)': '2026-02-06T15:45:00Z',
  },
  research_depth: 'deep',
  total_agents: 5,
  debate_rounds: 2,
  stance: 'BULLISH',
  bull_intensity: 72,
  bear_risk: 58,
  catalysts: [
    'Blackwell B200 production ramp (Q2 2026)',
    'Inference-as-a-Service revenue inflection',
    'Sovereign AI infrastructure contracts (Saudi Arabia, UAE, India)',
    'Next-gen Rubin architecture announcement (GTC 2026)',
  ],
  risks: [
    'Hyperscaler capex budget cuts in economic downturn',
    'China export restrictions tightening further',
    'AMD MI400 / Google TPUv6 competitive response',
    'DeepSeek-style efficiency gains reducing GPU demand growth',
  ],
  watch_items: [
    { trigger: 'Hyperscaler capex revisions in Q1 2026 earnings', impact: 'bear' as const, timeframe: 'April-May 2026', importance: 'high' as const },
    { trigger: 'AMD MI400 benchmark results', impact: 'bear' as const, timeframe: 'Q3 2026', importance: 'medium' as const },
    { trigger: 'China semiconductor policy changes', impact: 'bear' as const, timeframe: 'Ongoing', importance: 'high' as const },
  ],
};

export function buildFinancialExampleState(): FinancialStreamingState {
  return {
    phase: 'concluded',
    ticker: 'NVDA',
    depth: 'deep',
    hardDataSnapshot: EXAMPLE_SNAPSHOT,
    bullAgents: BULL_AGENTS,
    bearAgents: BEAR_AGENTS,
    debateMessages: DEBATE_MESSAGES,
    debateRound: 2,
    agreementScore: 85,
    mediatorCompilation: MEDIATOR_COMPILATION,
    killSheet: KILL_SHEET,
  };
}

